import { useMemo } from 'react';

import type { MessageInputContextValue } from '../../../context/MessageInputContext';
import type { CustomTrigger, DefaultStreamChatGenerics } from '../../../types/types';

export const useCreateMessageInputContext = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics,
  V extends CustomTrigger = CustomTrigger
>(
  value: MessageInputContextValue<StreamChatGenerics, V>,
) => {
  const {
    additionalTextareaProps,
    attachments,
    autocompleteTriggers,
    clearEditingState,
    closeCommandsList,
    closeEmojiPicker,
    closeMentionsList,
    cooldownInterval,
    cooldownRemaining,
    disabled,
    disableMentions,
    doFileUploadRequest,
    doImageUploadRequest,
    emojiIndex,
    emojiPickerIsOpen,
    emojiPickerRef,
    errorHandler,
    fileOrder,
    fileUploads,
    focus,
    grow,
    handleChange,
    handleEmojiKeyDown,
    handleSubmit,
    imageOrder,
    imageUploads,
    insertText,
    isUploadEnabled,
    maxFilesLeft,
    maxRows,
    mentionAllAppUsers,
    mentioned_users,
    mentionQueryParams,
    message,
    noFiles,
    numberOfUploads,
    onPaste,
    onSelectEmoji,
    onSelectUser,
    openCommandsList,
    openEmojiPicker,
    openMentionsList,
    overrideSubmitHandler,
    parent,
    publishTypingEvent,
    removeFile,
    removeImage,
    setCooldownRemaining,
    setText,
    shouldSubmit,
    showCommandsList,
    showMentionsList,
    text,
    textareaRef,
    uploadFile,
    uploadImage,
    uploadNewFiles,
    useMentionsTransliteration,
  } = value;

  const editing = message?.editing;
  const fileUploadsValue = Object.entries(fileUploads)
    // eslint-disable-next-line
    .map(([_, value]) => value.state)
    .join();
  const imageUploadsValue = Object.entries(imageUploads)
    // eslint-disable-next-line
    .map(([_, value]) => value.state)
    .join();
  const mentionedUsersLength = mentioned_users.length;
  const parentId = parent?.id;

  const messageInputContext: MessageInputContextValue<StreamChatGenerics, V> = useMemo(
    () => ({
      additionalTextareaProps,
      attachments,
      autocompleteTriggers,
      clearEditingState,
      closeCommandsList,
      closeEmojiPicker,
      closeMentionsList,
      cooldownInterval,
      cooldownRemaining,
      disabled,
      disableMentions,
      doFileUploadRequest,
      doImageUploadRequest,
      emojiIndex,
      emojiPickerIsOpen,
      emojiPickerRef,
      errorHandler,
      fileOrder,
      fileUploads,
      focus,
      grow,
      handleChange,
      handleEmojiKeyDown,
      handleSubmit,
      imageOrder,
      imageUploads,
      insertText,
      isUploadEnabled,
      maxFilesLeft,
      maxRows,
      mentionAllAppUsers,
      mentioned_users,
      mentionQueryParams,
      message,
      noFiles,
      numberOfUploads,
      onPaste,
      onSelectEmoji,
      onSelectUser,
      openCommandsList,
      openEmojiPicker,
      openMentionsList,
      overrideSubmitHandler,
      parent,
      publishTypingEvent,
      removeFile,
      removeImage,
      setCooldownRemaining,
      setText,
      shouldSubmit,
      showCommandsList,
      showMentionsList,
      text,
      textareaRef,
      uploadFile,
      uploadImage,
      uploadNewFiles,
      useMentionsTransliteration,
    }),
    [
      cooldownInterval,
      cooldownRemaining,
      editing,
      emojiPickerIsOpen,
      fileUploadsValue,
      imageUploadsValue,
      isUploadEnabled,
      mentionedUsersLength,
      parentId,
      publishTypingEvent,
      showCommandsList,
      showMentionsList,
      text,
    ],
  );

  return messageInputContext;
};
